import React, { useState, useEffect, useCallback } from 'react';
import {
  Title2,
  Body1,
  Card,
  Button,
  Input,
  Select,
  Spinner,
  Textarea,
  makeStyles,
  tokens,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
} from '@fluentui/react-components';
import {
  SearchRegular,
  EditRegular,
  ChatRegular,
  MailRegular,
  ClockRegular,
  ChevronDownRegular,
  ChevronUpRegular,
  CheckmarkRegular,
  DismissRegular,
  DataTrendingRegular,
  ArrowClockwiseRegular,
  ArrowDownloadRegular,
  PeopleRegular,
  DismissCircleRegular,
} from '@fluentui/react-icons';
import { useAuth } from '../hooks/useAuth';
import { getSharePointService } from '../services/sharePointService';
import { useDepartmentDirectors } from '../hooks/useDepartmentDirectors';
import { Volunteer, VolunteerStatus, Comment, getAvailableRoles, getBUManagerDisplay, getTeamsForDepartment, normalizeDepartment, getManagerRoleForTeam } from '../types/sharepoint';
import { UserRole } from '../types/auth';
import { useLocation } from 'react-router-dom';

// Canonical departments matching Org Chart (source of truth)
const CANONICAL_DEPARTMENTS = [
  'Marketing and Communications',
  'Clinical Operations',
  'Finance & Development',
  'LEHR',
  'Support Services',
  'Technology'
];

// Constants
const AREA_OPTIONS = [
  'Pre-Ops',
  'Ops Day Of',
  'Greeters',
  'Registration',
  'Intake',
  'Lab',
  'Exam Rooms',
  'Security',
  'Prayer',
  'Translator'
];

// Centralized in types/sharepoint.ts as BUSINESS_UNIT_LEADERS





const useStyles = makeStyles({
  container: {
    padding: '24px',
    maxWidth: '1200px',
    margin: '0 auto',
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    marginBottom: '32px',
    padding: '32px 24px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    background: `linear-gradient(135deg, ${tokens.colorNeutralBackground1} 0%, ${tokens.colorNeutralBackground2} 100%)`,
    borderRadius: '12px',
    boxShadow: tokens.shadow2,
  },
  headerTop: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
    '@media (min-width: 768px)': {
      flexDirection: 'row',
    justifyContent: 'space-between',
      alignItems: 'flex-start',
    },
  },
  titleContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  title: {
    margin: 0,
    fontSize: '28px',
    fontWeight: tokens.fontWeightBold,
    color: tokens.colorNeutralForeground1,
    lineHeight: '1.2',
    letterSpacing: '-0.02em',
    marginBottom: '8px',
  },
  subtitle: {
    color: tokens.colorNeutralForeground3,
    fontSize: '14px',
    fontWeight: tokens.fontWeightRegular,
    lineHeight: '1.4',
    margin: 0,
  },
  filters: {
    display: 'flex',
    gap: '16px',
    marginBottom: '24px',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'center',
    '@media (max-width: 768px)': {
      flexDirection: 'column',
      gap: '12px',
      alignItems: 'stretch',
    },
  },
  searchInput: {
    minWidth: '200px',
  },
  filterSelect: {
    minWidth: '150px',
  },
  stats: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '16px',
    marginBottom: '24px',
  },
  statCard: {
    padding: '20px',
    borderRadius: '12px',
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    boxShadow: tokens.shadow2,
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    transition: 'all 0.2s',
    ':hover': {
      boxShadow: tokens.shadow4,
      transform: 'translateY(-2px)',
    },
  },
  statIcon: {
    width: '40px',
    height: '40px',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '20px',
    flexShrink: 0,
  },
  statContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '4px',
  },
  statValue: {
    fontSize: '28px',
    fontWeight: tokens.fontWeightBold,
    color: tokens.colorNeutralForeground1,
    lineHeight: '1',
  },
  statLabel: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground3,
    fontWeight: tokens.fontWeightMedium,
  },
  volunteerGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(420px, 1fr))', // widened card width
    gap: '16px',
    '@media (max-width: 768px)': {
      gridTemplateColumns: '1fr',
      gap: '12px',
    },
  },
  volunteerCard: {
    height: '460px', // increased to fit phone row and extra content
    display: 'flex',
    flexDirection: 'column',
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    flex: 1,
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexDirection: 'column',
    gap: '8px',
  },
  volunteerInfo: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
  },
  modalVolunteerInfo: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
    marginBottom: '20px',
    paddingBottom: '16px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  headerMeta: {
    display: 'flex',
    gap: '8px',
    flexWrap: 'wrap',
    marginTop: '6px',
  },
  headerMetaItem: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground2,
    backgroundColor: tokens.colorNeutralBackground2,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: '12px',
    padding: '2px 8px',
  },
  volunteerAvatar: {
    width: '40px',
    height: '40px',
    backgroundColor: tokens.colorBrandBackground,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
    fontWeight: tokens.fontWeightSemibold,
    color: 'white',
    flexShrink: 0,
  },
  volunteerName: {
    fontSize: '16px',
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    marginBottom: '4px',
  },
  volunteerStatus: {
    marginLeft: 'auto',
    flexShrink: 0,
  },
  quickReadContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    flex: 1,
  },
  statusField: {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 12px',
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: '4px',
    fontSize: '14px',
  },
  statusLabel: {
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground2,
    marginRight: '8px',
  },
  statusValue: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    flex: 1,
  },
  statusBadge: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '2px 8px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: tokens.fontWeightSemibold,
  },
  roleTag: {
    display: 'inline-flex',
    alignItems: 'center',
    padding: '4px 8px',
    borderRadius: '12px',
    fontSize: '12px',
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground1,
    marginRight: '4px',
    marginBottom: '4px',
  },
  volunteerHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '12px',
  },
  volunteerContact: {
    display: 'flex',
    gap: '16px',
  },
  contactItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  contactIcon: {
    fontSize: '16px',
  },
  complianceStatus: {
    display: 'flex',
    gap: '16px',
  },
  schedulingStatus: {
    display: 'flex',
    gap: '16px',
  },
  managementStatus: {
    display: 'flex',
    gap: '16px',
  },
  executiveStatus: {
    display: 'flex',
    gap: '16px',
  },
  statusItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  detailsSection: {
    marginTop: '16px',
    padding: '12px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: '6px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  roleSpecificSection: {
    marginBottom: '16px',
  },
  roleSectionTitle: {
    fontSize: '14px',
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
    marginBottom: '8px',
  },
  complianceGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
    gap: '12px',
    marginBottom: '16px',
  },
  checkboxItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
  },
  complianceCheckbox: {
    width: '18px',
    height: '18px',
  },
  complianceDetails: {
    marginTop: '16px',
  },
  additionalDetails: {
    marginTop: '16px',
  },
  detailItem: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '8px 0',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  detailLabel: {
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground2,
  },
  detailValue: {
    color: tokens.colorNeutralForeground1,
  },
  cardActions: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '16px',
    paddingTop: '16px',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  formField: {
    marginBottom: '16px',
  },
  formFieldLabel: {
    display: 'block',
    marginBottom: '4px',
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground1,
  },
  formInput: {
    width: '100%',
    padding: '8px 12px',
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: '4px',
    fontSize: '14px',
  },
  headerActions: {
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
    alignItems: 'flex-end',
    '@media (min-width: 768px)': {
      alignItems: 'flex-end',
    },
  },
  commentsSection: {
    marginTop: '12px',
    padding: '12px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
  },
  commentsHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '12px',
  },
  commentsLoading: {
    textAlign: 'center',
    padding: '12px',
    color: tokens.colorNeutralForeground2,
  },
  commentsList: {
    marginBottom: '12px',
    maxHeight: '300px',
    overflowY: 'auto',
    paddingRight: '8px',
  },
  commentItem: {
    padding: '12px 0',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  commentHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '4px',
  },
  commentAuthor: {
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  commentDate: {
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground3,
  },
  commentContent: {
    color: tokens.colorNeutralForeground1,
    lineHeight: tokens.lineHeightBase300,
    marginTop: '4px',
    wordBreak: 'break-word',
  },
  noComments: {
    textAlign: 'center',
    padding: '12px',
    color: tokens.colorNeutralForeground2,
    fontStyle: 'italic',
  },
  addCommentSection: {
    marginTop: '12px',
    paddingTop: '12px',
    borderTop: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  commentInput: {
    width: '100%',
    marginBottom: '12px',
  },
  commentActions: {
    display: 'flex',
    gap: '8px',
  },
  fullScreenModal: {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  },
  modalOverlay: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: -1,
  },
  modalContent: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    boxShadow: tokens.shadow4,
    width: '95%',
    maxWidth: '1400px',
    maxHeight: '95%',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 1,
  },
  modalHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '16px 24px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  modalHeaderContent: {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  },
  volunteerAvatarLarge: {
    width: '60px',
    height: '60px',
    borderRadius: '50%',
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    overflow: 'hidden',
  },
  avatarImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  avatarPlaceholder: {
    width: '100%',
    height: '100%',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '24px',
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  volunteerInfoCentered: {
    display: 'flex',
    flexDirection: 'column',
  },
  volunteerNameLarge: {
    margin: 0,
    fontSize: '24px',
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  modalBody: {
    flex: 1,
    overflowY: 'auto',
    padding: '24px',
  },
  modalDetails: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  modalComments: {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  detailsCommentsSection: {
    marginTop: '16px',
    padding: '12px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: '6px',
    border: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  avatarModalContent: {
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: tokens.borderRadiusLarge,
    boxShadow: tokens.shadow4,
    width: '90%',
    maxWidth: '600px',
    maxHeight: '80%',
    display: 'flex',
    flexDirection: 'column',
    zIndex: 1,
  },
  avatarUploadSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '24px',
    padding: '24px',
  },
  currentAvatar: {
    textAlign: 'center',
  },
  avatarPreview: {
    width: '150px',
    height: '150px',
    margin: '0 auto 16px',
    borderRadius: '50%',
    overflow: 'hidden',
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    backgroundColor: tokens.colorNeutralBackground1,
  },
  avatarPreviewImage: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  avatarPreviewPlaceholder: {
    width: '100%',
    height: '100%',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '40px',
    fontWeight: tokens.fontWeightSemibold,
    color: tokens.colorNeutralForeground1,
  },
  uploadSection: {
    textAlign: 'center',
  },
  fileInput: {
    display: 'none',
  },
  fileInputLabel: {
    display: 'inline-block',
    padding: '8px 16px',
    backgroundColor: tokens.colorBrandBackground,
    color: 'white',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: tokens.fontWeightSemibold,
    border: `1px solid ${tokens.colorBrandBackground}`,
  },
  filePreview: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginTop: '16px',
    padding: '12px',
    backgroundColor: tokens.colorNeutralBackground1,
    borderRadius: '6px',
    border: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  filePreviewImage: {
    width: '50px',
    height: '50px',
    objectFit: 'cover',
    borderRadius: '4px',
  },
  fileInfo: {
    fontSize: '12px',
    color: tokens.colorNeutralForeground3,
  },
  uploadActions: {
    display: 'flex',
    justifyContent: 'center',
    gap: '12px',
    marginTop: '24px',
  },
  statusBadgeSection: {
    marginTop: '12px',
    marginBottom: '12px',
  },
  lastUpdated: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 16px',
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: '20px',
    border: `1px solid ${tokens.colorNeutralStroke1}`,
  },
  lastUpdatedText: {
    fontSize: '14px',
    color: tokens.colorNeutralForeground3,
    fontWeight: tokens.fontWeightMedium,
  },
  actionButton: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    padding: '10px 16px', // Increased from 8px 12px
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: tokens.fontWeightMedium,
    color: tokens.colorNeutralForeground1,
    cursor: 'pointer',
    transition: 'all 0.2s',
    ':hover': {
      backgroundColor: tokens.colorNeutralBackground2,
      border: `1px solid ${tokens.colorNeutralStroke2}`,
    },
  },
  actionButtons: {
    display: 'flex',
    gap: '12px', // Increased from 8px
    justifyContent: 'flex-end',
  },
  adminToggle: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginBottom: '8px',
  },
  activeToggle: {
    backgroundColor: tokens.colorBrandBackground,
    color: tokens.colorNeutralForegroundOnBrand,
    border: `1px solid ${tokens.colorBrandStroke1}`,
  },


  modalTabs: {
    display: 'flex',
    gap: '8px',
    marginBottom: '16px',
    padding: '16px 24px 0',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  wideDialogSurface: {
    width: '1200px',
    maxWidth: '95vw',
    maxHeight: '90vh',
  },
  noBorderDialogTitle: {
    borderBottom: 'none',
  },
  contactInfo: {
    marginBottom: '24px',
    paddingBottom: '24px',
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  volunteerDetails: {
    flex: 1,
  },
  modalAvatar: {
    width: '80px', // Big avatar for modal
    height: '80px', // Big avatar for modal
    backgroundColor: tokens.colorBrandBackground,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '32px', // Big font for modal
    fontWeight: tokens.fontWeightSemibold,
    color: 'white',
    flexShrink: 0,
  },
});

// Role-specific volunteer card component
const RoleSpecificVolunteerCard: React.FC<{
  volunteer: Volunteer;
  role: UserRole;
  onUpdate: (id: string, updates: Partial<Volunteer>) => Promise<void>;
  adminViewMode?: 'manager' | 'onboarding';
}> = ({ volunteer, role, onUpdate, adminViewMode }) => {
  const styles = useStyles();
  const { hasRole } = useAuth();
  const [comments, setComments] = useState<Comment[]>([]);
  const [loadingComments, setLoadingComments] = useState(false);
  const [submittingComment, setSubmittingComment] = useState(false);
  const [newComment, setNewComment] = useState('');
  // Removed showComments state as it's not being used

  
  // Refs for smooth scrolling
  const cardRef = React.useRef<HTMLDivElement>(null);
  
  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showAvatarModal, setShowAvatarModal] = useState(false);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [commentsExpanded, setCommentsExpanded] = useState(false);
  const [isEditingContact, setIsEditingContact] = useState(false);
  const [roleToLead, setRoleToLead] = useState<Record<string, string>>({});
  const { getDirectorsForDepartment } = useDepartmentDirectors();

  // Build role -> lead name map once, to resolve Department Lead by selected team (matching OrgChart approach)
  useEffect(() => {
    (async () => {
      try {
        const data = await getSharePointService().getVolunteers();
        const map: Record<string, string> = {};
        data.forEach(v => {
          if (v.assignedRole && v.name && !map[v.assignedRole]) {
            map[v.assignedRole] = v.name;
          }
        });
        setRoleToLead(map);
      } catch {
        // ignore errors; UI will display fallback
      }
    })();
  }, []);

  // Get team lead display matching OrgChart approach (using normalizeDepartment and getManagerRoleForTeam)
  const getTeamLeadDisplay = (v: Volunteer): string => {
    const normalizedDept = normalizeDepartment(v.businessUnit);
    const team = v.secondaryArea;
    
    // Validate team exists in department (matching OrgChart strict validation)
    if (normalizedDept && team) {
      const deptTeams = getTeamsForDepartment(normalizedDept);
      const teamExists = deptTeams.some(t => t.toLowerCase() === team.toLowerCase());
      
      if (teamExists) {
        // Use getManagerRoleForTeam helper (which uses normalizeDepartment internally)
        const role = getManagerRoleForTeam(normalizedDept, team);
        if (role) {
          return roleToLead[role] || 'Not assigned';
        }
      }
    }
    
    // Fallback: show TBD (matching OrgChart behavior) instead of BU Manager
    return 'TBD';
  };

  // Get department directors (matching OrgChart approach)
  const getDepartmentDirectorsDisplay = (dept?: string): string => {
    if (!dept) return 'Not assigned';
    const normalizedDept = normalizeDepartment(dept);
    if (!normalizedDept) return 'Not assigned';
    const directors = getDirectorsForDepartment(normalizedDept);
    if (directors.length === 0) return 'Not assigned';
    return directors.join(', ');
  };










  const handleComplianceUpdate = async (id: string, field: string, value: boolean | string) => {
    console.log('🔍 handleComplianceUpdate called with:', { id, field, value });
    console.log('🔍 Current volunteer data:', volunteer);
    
    try {
      console.log('🔍 Calling onUpdate with:', { [field]: value });
      await onUpdate(id, { [field]: value });
      console.log('✅ Compliance update completed successfully');
    } catch (error) {
      console.error('❌ Error updating compliance status:', error);
    }
  };

  const handleEmailClick = (email: string) => {
    window.open(`mailto:${email}`, '_blank');
  };

  const handleWhatsAppClick = (phone: string) => {
    const cleanPhone = phone.replace(/\D/g, '');
    window.open(`https://wa.me/${cleanPhone}`, '_blank');
  };





  const handleAvatarUpload = async () => {
    if (!avatarFile) return;
    
    try {
      setUploadingAvatar(true);
      // TODO: Implement avatar upload to SharePoint
      console.log('Uploading avatar for volunteer:', volunteer.id);
      
      // For now, just simulate the upload
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Update the volunteer with the new avatar URL
      await onUpdate(volunteer.id, { 
        avatarUrl: URL.createObjectURL(avatarFile) 
      });
      
      setShowAvatarModal(false);
      setAvatarFile(null);
    } catch (error) {
      console.error('Error uploading avatar:', error);
    } finally {
      setUploadingAvatar(false);
    }
  };

  // Load comments for a volunteer from the Volunteer Comments list
  const loadComments = useCallback(async () => {
    try {
      setLoadingComments(true);
      
      // Check if the Volunteer Comments list exists, create it if it doesn't
      const service = getSharePointService();
      // Use existing Volunteer Comments list - no creation needed
      console.log('ℹ️ Using existing Volunteer Comments list');
      
      const volunteerComments = await service.getVolunteerCommentsFromList(volunteer.id);
      setComments(volunteerComments);
    } catch (error) {
      console.error('Error loading comments:', error);
      // Don't retry on error to prevent infinite loops
    } finally {
      setLoadingComments(false);
    }
  }, [volunteer.id]); // Only depend on volunteer.id, not the entire volunteer object

  // Add a new comment to the Volunteer Comments list
  const handleAddComment = async () => {
    if (!newComment.trim()) return;
    
    try {
      setSubmittingComment(true);
      const service = getSharePointService();
      
      // Add the comment using the regular method
      await service.addVolunteerCommentToList(volunteer.id, volunteer.name, newComment);
      
      setNewComment('');
      // Reload comments
      await loadComments();
      console.log('✅ Comment added successfully');
    } catch (error) {
      console.error('❌ Error adding comment:', error);
      // You could add a toast notification here
      alert('Failed to add comment. Please try again.');
    } finally {
      setSubmittingComment(false);
    }
  };

  // Toggle comments section
  const toggleComments = () => {
    setIsModalOpen(true);
    setCommentsExpanded(true);
    // Load comments when opening comments modal
    loadComments();
  };

  // Toggle details section with smooth scrolling
  const toggleDetails = () => {
    setIsModalOpen(true);
    // Load comments when opening details
    loadComments();
  };

  // Remove the automatic useEffect that was causing infinite loops
  // useEffect(() => {
  //   loadComments();
  // }, [loadComments]);

  // Quick-read card content based on role
  const renderQuickReadContent = () => {

    // Handle admin view mode
    const effectiveRole = role === 'admin' ? (adminViewMode || 'manager') : role;

    switch (effectiveRole) {
      case 'onboarding':
        return (
          <div className={styles.quickReadContent}>
            <div className={styles.statusField}>
              <span className={styles.statusLabel}>Contact Status:</span>
              <Select
                value={volunteer.volunteerStatus || ''}
                onChange={(e, data) => handleComplianceUpdate(volunteer.id, 'volunteerStatus', data.value || '')}
                style={{ width: '100%', fontSize: '14px' }}
              >
                <option value={VolunteerStatus.NEW_NEEDS_CONTACT}>New - Needs Contact</option>
                <option value={VolunteerStatus.CONTACT_COMPLETE}>Active</option>
                <option value={VolunteerStatus.CONTACTED_NO_RESPONSE}>Contacted - No Response</option>
                <option value={VolunteerStatus.INACTIVE}>Inactive</option>
              </Select>
              </div>
            <div className={styles.statusField}>
              <span className={styles.statusLabel}>Clinic Day Area:</span>
              <Select
                value={volunteer.primaryArea || ''}
                onChange={(e, data) => handleComplianceUpdate(volunteer.id, 'primaryArea', data.value || '')}
                style={{ width: '100%', fontSize: '14px' }}
              >
                <option value="">Select Clinic Day Area</option>
                {AREA_OPTIONS.map(area => (
                  <option key={area} value={area}>{area}</option>
                ))}
              </Select>
            </div>
            <div className={styles.statusField}>
              <span className={styles.statusLabel}>Onboarding Group:</span>
              <Select
                value={volunteer.assignedManager || ''}
                onChange={(e, data) => handleComplianceUpdate(volunteer.id, 'assignedManager', data.value || '')}
                style={{ width: '100%', fontSize: '14px' }}
              >
                <option value="">Select Onboarding Group</option>
                <option value="Compliance">Compliance</option>
                <option value="Scheduling">Scheduling</option>
                <option value="Verification">Verification</option>
              </Select>
            </div>
            <div className={styles.statusField}>
              <span className={styles.statusLabel}>Phone:</span>
              <span className={styles.statusValue}>{volunteer.phone || 'Not provided'}</span>
            </div>
          </div>
        );
      case 'scheduler':
        return (
          <div className={styles.quickReadContent}>
            <div className={styles.statusField}> 
              <div className={styles.statusLabel}>Availability Status</div>
              <div className={styles.statusValue}>
                {volunteer.augustAvailability || 'No data'}
              </div>
            </div>
            <div className={styles.statusField}> 
              <div className={styles.statusLabel}>Clinic Day Area</div>
              <Select
                value={volunteer.primaryArea || ''}
                onChange={(e, data) => handleComplianceUpdate(volunteer.id, 'primaryArea', data.value || '')}
                style={{ width: '100%', fontSize: '14px' }}
              >
                <option value="">Select Clinic Day Area</option>
                {AREA_OPTIONS.map(area => (
                  <option key={area} value={area}>{area}</option>
                ))}
              </Select>
            </div>
            <div className={styles.statusField}>
              <span className={styles.statusLabel}>Phone:</span>
              <span className={styles.statusValue}>{volunteer.phone || 'Not provided'}</span>
            </div>
          </div>
        );
      case 'manager':
        return (
          <div className={styles.quickReadContent}>
            <div className={styles.statusField}> 
              <span className={styles.statusLabel}>Clinic Day Area:</span>
              <Select
                value={volunteer.primaryArea || ''}
                onChange={(e, data) => handleComplianceUpdate(volunteer.id, 'primaryArea', data.value || '')}
                style={{ width: '100%', fontSize: '14px' }}
              >
                <option value="">Select Clinic Day Area</option>
                {AREA_OPTIONS.map(area => (
                  <option key={area} value={area}>{area}</option>
                ))}
              </Select>
            </div>
            <div className={styles.statusField}> 
              <span className={styles.statusLabel}>Department:</span>
              <span className={styles.statusValue}>{volunteer.businessUnit || 'Not assigned'}</span>
              </div>
            <div className={styles.statusField}>
              <span className={styles.statusLabel}>Department Lead:</span>
              <span className={styles.statusValue}>{getTeamLeadDisplay(volunteer)}</span>
              </div>
            <div className={styles.statusField}>
              <span className={styles.statusLabel}>Role:</span>
              <span className={styles.statusValue}>{volunteer.assignedRole || 'Not assigned'}</span>
            </div>
            <div className={styles.statusField}>
              <span className={styles.statusLabel}>Phone:</span>
              <span className={styles.statusValue}>{volunteer.phone || 'Not provided'}</span>
            </div>
          </div>
        );
      case 'executive':
        return (
          <div className={styles.quickReadContent}>
            <div className={styles.statusField}>
              <span className={styles.statusLabel}>Department:</span>
              <span className={styles.statusValue}>{volunteer.businessUnit || 'Not assigned'}</span>
            </div>
            <div className={styles.statusField}>
              <span className={styles.statusLabel}>Department Director:</span>
              <span className={styles.statusValue}>{getDepartmentDirectorsDisplay(volunteer.businessUnit)}</span>
            </div>
            <div className={styles.statusField}>
              <span className={styles.statusLabel}>Department Lead:</span>
              <span className={styles.statusValue}>{getTeamLeadDisplay(volunteer)}</span>
            </div>
            <div className={styles.statusField}>
              <span className={styles.statusLabel}>Phone:</span>
              <span className={styles.statusValue}>{volunteer.phone || 'Not provided'}</span>
            </div>
          </div>
        );
      default:
        return (
          <div className={styles.quickReadContent}>
            <div className={styles.statusField}>
              <span className={styles.statusLabel}>Contact Status:</span>
              <Select
                value={volunteer.volunteerStatus || ''}
                onChange={(e, data) => handleComplianceUpdate(volunteer.id, 'volunteerStatus', data.value || '')}
                style={{ width: '100%', fontSize: '14px' }}
              >
                <option value={VolunteerStatus.NEW_NEEDS_CONTACT}>New - Needs Contact</option>
                <option value={VolunteerStatus.CONTACT_COMPLETE}>Active</option>
                <option value={VolunteerStatus.CONTACTED_NO_RESPONSE}>Contacted - No Response</option>
                <option value={VolunteerStatus.INACTIVE}>Inactive</option>
              </Select>
            </div>
            <div className={styles.statusField}>
              <span className={styles.statusLabel}>Clinic Day Area:</span>
              <Select
                value={volunteer.primaryArea || ''}
                onChange={(e, data) => handleComplianceUpdate(volunteer.id, 'primaryArea', data.value || '')}
                style={{ width: '100%', fontSize: '14px' }}
              >
                <option value="">Select Clinic Day Area</option>
                {AREA_OPTIONS.map(area => (
                  <option key={area} value={area}>{area}</option>
                ))}
              </Select>
            </div>
            <div className={styles.statusField}>
              <span className={styles.statusLabel}>Phone:</span>
              <span className={styles.statusValue}>{volunteer.phone || 'Not provided'}</span>
            </div>
          </div>
        );
    }
  };



  return (
    <Card key={volunteer.id} className={styles.volunteerCard} ref={cardRef}>
      <div className={styles.cardContent}>
        <div className={styles.cardHeader}>
          <div className={styles.volunteerInfo}>
            <div className={styles.volunteerAvatar}>
              {volunteer.name?.charAt(0)?.toUpperCase() || 'V'}
            </div>
            <div>
              <div className={styles.volunteerName}>
                {volunteer.name}
                {(() => {
                  try {
                    if (!volunteer.createdAt) return null;
                    const created = new Date(volunteer.createdAt as string);
                    const days = Math.floor((Date.now() - created.getTime()) / (1000 * 60 * 60 * 24));
                    if (days <= 90) {
                      return <span style={{
                        marginLeft: 6,
                        backgroundColor: tokens.colorPaletteGreenBackground2,
                        color: tokens.colorPaletteGreenForeground2,
                        borderRadius: '10px',
                        padding: '1px 6px',
                        fontSize: '9px',
                        fontWeight: tokens.fontWeightSemibold,
                      }}>NEW</span>;
                    }
                    return null;
                  } catch { return null; }
                })()}
              </div>
            </div>
          </div>
        </div>

        {renderQuickReadContent()}

        {/* Comments Section - Now handled by modal */}
        {/* {showComments && (
          <div className={styles.commentsSection} ref={commentsRef}>
            <div className={styles.commentsHeader}>
              <h4>Volunteer Comments ({comments.length})</h4>
              <Button
                appearance="subtle"
                size="small"
                onClick={() => setShowComments(false)}
              >
                Hide Comments
              </Button>
            </div>
            
            {loadingComments ? (
              <div className={styles.commentsLoading}>
                <Spinner size="small" label="Loading comments..." />
              </div>
            ) : comments.length > 0 ? (
              <div className={styles.commentsList}>
                {comments.map((comment) => (
                  <div key={comment.id} className={styles.commentItem}>
                    <div className={styles.commentHeader}>
                      <span className={styles.commentAuthor}>
                        {comment.author}
                      </span>
                      <span className={styles.commentDate}>
                        {new Date(comment.timestamp).toLocaleDateString()} at {new Date(comment.timestamp).toLocaleTimeString()}
                      </span>
                    </div>
                    <div className={styles.commentContent}>
                      {comment.content}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className={styles.noComments}>
                No comments yet. Add the first comment below.
              </div>
            )}
            
            <div className={styles.addCommentSection}>
              <Textarea
                value={newComment}
                onChange={(e) => setNewComment(e.target.value)}
                placeholder="Add a comment about this volunteer..."
                rows={3}
                className={styles.commentInput}
                disabled={submittingComment}
              />
              <div className={styles.commentActions}>
                <Button
                  appearance="primary"
                  size="small"
                  onClick={handleAddComment}
                  disabled={!newComment.trim() || submittingComment}
                >
                  {submittingComment ? (
                    <>
                      <Spinner size="tiny" />
                      Adding...
                    </>
                  ) : (
                    'Add Comment'
                  )}
                </Button>
                <Button
                  appearance="subtle"
                  size="small"
                  onClick={() => setNewComment('')}
                  disabled={submittingComment}
                >
                  Clear
                </Button>
              </div>
            </div>
          </div>
        )} */}
      </div>

      <div className={styles.cardActions}>
        <div className={styles.actionButtons}>
          {/* Universal buttons for all roles */}
          <Button
            appearance="subtle"
            icon={<ChatRegular />}
            onClick={toggleComments}
            size="small"
          >
            Comments ({volunteer.commentCount || 0})
          </Button>
          
          {volunteer.email && (
            <Button
              appearance="subtle"
              icon={<MailRegular />}
              onClick={() => handleEmailClick(volunteer.email)}
              size="small"
            >
              Email
            </Button>
          )}
          
          {volunteer.phone && (
            <Button
              appearance="subtle"
              icon={<ChatRegular />}
              onClick={() => handleWhatsAppClick(volunteer.phone)}
              size="small"
            >
              WhatsApp
            </Button>
          )}
        </div>
        <Button
          appearance="subtle"
          onClick={toggleDetails}
          size="small"
        >
          View Details
        </Button>

      </div>


      
      {/* Volunteer Details Modal */}
      {isModalOpen && (
        <Dialog open={isModalOpen} onOpenChange={() => setIsModalOpen(false)}>
          <DialogSurface className={styles.wideDialogSurface}>
            <DialogBody>
              <DialogTitle className={styles.noBorderDialogTitle}>
                <div style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  width: '100%'
                }}>
                  <span>Volunteer Details</span>
                  <Button
                    appearance="subtle"
                    icon={<DismissRegular />}
                    onClick={() => setIsModalOpen(false)}
                    size="small"
                  />
                </div>
              </DialogTitle>
              <DialogContent style={{ maxHeight: 'calc(90vh - 120px)', overflowY: 'auto' }}>
                <div className={styles.modalContent}>
                  <div className={styles.modalVolunteerInfo}>
                    <div className={styles.modalAvatar}>
                      {volunteer.name?.charAt(0)?.toUpperCase() || 'V'}
                    </div>
                    <div className={styles.volunteerDetails}>
                      <div style={{ fontWeight: tokens.fontWeightSemibold, fontSize: '16px' }}>
                        {volunteer.name || 'Unknown Volunteer'}
                      </div>
                      {volunteer.createdAt && (
                        <div style={{ fontSize: '12px', color: tokens.colorNeutralForeground3 }}>
                          Joined {(() => {
                            try {
                              const created = new Date(volunteer.createdAt as string);
                              const days = Math.floor((Date.now() - created.getTime()) / (1000 * 60 * 60 * 24));
                              return `${days} day${days === 1 ? '' : 's'} ago`;
                            } catch { return '—'; }
                          })()}
                        </div>
                      )}
                      
                    </div>
                  </div>
                  
                  {/* Contact Information and Serving Areas */}
                  <div className={styles.contactInfo}>
                    <div style={{ 
                      position: 'relative',
                      display: 'grid',
                      gridTemplateColumns: '1fr 1px 1fr',
                      columnGap: '24px',
                      alignItems: 'start'
                    }}>
                      {/* Contact Information - Left Half */}
                      <div style={{ 
                        paddingRight: '24px'
                      }}>
                        <div style={{ 
                          display: 'flex', 
                          justifyContent: 'space-between', 
                          alignItems: 'center', 
                          marginBottom: '8px' 
                        }}>
                          <div style={{ fontWeight: tokens.fontWeightSemibold }}>
                          Contact Information
                        </div>
                          <Button
                            appearance="subtle"
                            size="small"
                            icon={isEditingContact ? <CheckmarkRegular /> : <EditRegular />}
                            onClick={() => setIsEditingContact(!isEditingContact)}
                          >
                            {isEditingContact ? 'Save' : 'Edit'}
                          </Button>
              </div>
                        
                        {/* Email Field */}
                        <div style={{ marginBottom: '12px' }}>
                          <div style={{ fontSize: '12px', color: tokens.colorNeutralForeground3, marginBottom: '4px' }}>
                            Email
                          </div>
                          {isEditingContact ? (
                            <Input
                              value={volunteer.email || ''}
                              onChange={(e) => handleComplianceUpdate(volunteer.id, 'email', e.target.value)}
                              placeholder="Enter email address"
                              style={{ width: '100%' }}
                            />
                          ) : (
                            <div style={{ 
                              fontSize: '14px', 
                              color: tokens.colorNeutralForeground1,
                              padding: '8px 12px',
                              border: `1px solid ${tokens.colorNeutralStroke2}`,
                              borderRadius: '4px',
                              backgroundColor: tokens.colorNeutralBackground1
                            }}>
                              {volunteer.email || 'No email provided'}
                </div>
                        )}
                        </div>
                        
                        {/* Phone Field */}
                        <div style={{ marginBottom: '12px' }}>
                          <div style={{ fontSize: '12px', color: tokens.colorNeutralForeground3, marginBottom: '4px' }}>
                            Phone Number
                          </div>
                          {isEditingContact ? (
                            <Input
                              value={volunteer.phone || ''}
                              onChange={(e) => {
                                const phoneValue = e.target.value;
                                handleComplianceUpdate(volunteer.id, 'phone', phoneValue);
                                // Automatically update WhatsApp to match phone number
                                handleComplianceUpdate(volunteer.id, 'whatsapp', phoneValue);
                              }}
                              placeholder="Enter phone number"
                              style={{ width: '100%' }}
                            />
                          ) : (
                            <div style={{ 
                              fontSize: '14px', 
                              color: tokens.colorNeutralForeground1,
                              padding: '8px 12px',
                              border: `1px solid ${tokens.colorNeutralStroke2}`,
                              borderRadius: '4px',
                              backgroundColor: tokens.colorNeutralBackground1
                            }}>
                              {volunteer.phone || 'No phone number provided'}
                </div>
                        )}
                        </div>
                        
                        {/* Department Director - Added underneath telephone number */}
                        <div style={{ marginTop: '12px', paddingTop: '12px' }}>
                          <div style={{ fontWeight: tokens.fontWeightSemibold, marginBottom: '8px' }}>
                            Department Director
              </div>
                          <div style={{ 
                            fontSize: '14px', 
                            color: tokens.colorNeutralForeground1,
                            padding: '8px 12px',
                            border: `1px solid ${tokens.colorNeutralStroke2}`,
                            borderRadius: '4px',
                            backgroundColor: tokens.colorNeutralBackground1
                          }}>
                            {getDepartmentDirectorsDisplay(volunteer.businessUnit)}
                          </div>
                        </div>
                        
                        {/* Manager - Moved to left half near Department Director */}
                        <div style={{ marginTop: '12px' }}>
                          <div style={{ fontWeight: tokens.fontWeightSemibold, marginBottom: '8px' }}>
                            Manager
                          </div>
                          <div style={{ 
                            fontSize: '14px', 
                            color: tokens.colorNeutralForeground1,
                            padding: '8px 12px',
                            border: `1px solid ${tokens.colorNeutralStroke2}`,
                            borderRadius: '4px',
                            backgroundColor: tokens.colorNeutralBackground1
                          }}>
                            {getBUManagerDisplay(volunteer) || 'Not assigned'}
                          </div>
                        </div>
                      </div>
                      
                      {/* Vertical divider */}
                      <div aria-hidden style={{ width: '1px', backgroundColor: tokens.colorNeutralStroke2 }} />

                      {/* Serving Areas - Right Half */}
                      <div style={{ paddingLeft: '24px' }}>
                        <div style={{ fontWeight: tokens.fontWeightSemibold, marginBottom: '8px' }}>
                          Serving Areas
                        </div>
                        
                        {/* Primary Area and Department - Side by side */}
                        <div style={{ 
                          display: 'flex', 
                          gap: '12px', 
                          marginBottom: '12px',
                          alignItems: 'flex-start'
                        }}>
                          {/* Clinic Day Area */}
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '12px', color: tokens.colorNeutralForeground3, marginBottom: '4px' }}>
                              Clinic Day Area
                            </div>
                            <Select
                              value={volunteer.primaryArea || ''}
                              onChange={(e, data) => {
                                handleComplianceUpdate(volunteer.id, 'primaryArea', data.value || '');
                              }}
                              style={{ width: '100%' }}
                            >
                              <option value="">Select Clinic Day Area</option>
                              {AREA_OPTIONS.map(area => (
                                <option key={area} value={area}>{area}</option>
                              ))}
                            </Select>
            </div>
            
                          {/* Department - Next to Primary Area */}
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '12px', color: tokens.colorNeutralForeground3, marginBottom: '4px' }}>
                              Department
                            </div>
                            <Select
                              value={volunteer.businessUnit || ''}
                              onChange={(e, data) => {
                                handleComplianceUpdate(volunteer.id, 'businessUnit', data.value || '');
                              }}
                              style={{ width: '100%' }}
                            >
                              <option value="">Select Department</option>
                              {CANONICAL_DEPARTMENTS.map(unit => (
                                <option key={unit} value={unit}>{unit}</option>
                              ))}
                            </Select>
                          </div>
                        </div>
                        
                        {/* Secondary Area and Role - Side by side */}
                        <div style={{ 
                          display: 'flex', 
                          gap: '12px', 
                          marginBottom: '12px',
                          alignItems: 'flex-start'
                        }}>
                          {/* Team */}
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '12px', color: tokens.colorNeutralForeground3, marginBottom: '4px' }}>
                              Team
                            </div>
                            <Select
                              value={volunteer.secondaryArea || ''}
                              onChange={async (e, data) => {
                                const team = data.value || '';
                                // Update team
                                await handleComplianceUpdate(volunteer.id, 'secondaryArea', team);
                                
                                // Auto-assign Department Lead role if team is selected and role is empty or "Department Lead"
                                if (team && volunteer.businessUnit) {
                                  const normalizedDept = normalizeDepartment(volunteer.businessUnit);
                                  const teamLeadRole = getManagerRoleForTeam(normalizedDept, team);
                                  if (teamLeadRole && (!volunteer.assignedRole || volunteer.assignedRole === 'Department Lead' || volunteer.assignedRole.includes('Lead') || volunteer.assignedRole.includes('Manager'))) {
                                    // Auto-assign the specific team lead role
                                    await handleComplianceUpdate(volunteer.id, 'assignedRole', teamLeadRole);
                                  }
                                }
                              }}
                              style={{ width: '100%' }}
                            >
                              <option value="">Select Team</option>
                              {getTeamsForDepartment(volunteer.businessUnit).map(team => (
                                <option key={team} value={team}>{team}</option>
                              ))}
                            </Select>
                          </div>
                          
                          {/* Role - Beside Team */}
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '12px', color: tokens.colorNeutralForeground3, marginBottom: '4px' }}>
                              Role
                            </div>
                            <Select
                              value={(() => {
                                // Map actual role to display role
                                const role = volunteer.assignedRole || '';
                                if (!role) return '';
                                if (role === 'Board Member') return 'Board Member';
                                if (role === 'Department Director') return 'Director';
                                if (role.includes('Lead') || role.includes('Manager')) return 'Department Lead';
                                return 'Volunteer';
                              })()}
                              onChange={async (e, data) => {
                                const selectedRole = data.value || '';
                                let actualRole = selectedRole;
                                
                                // Map display role to actual role value
                                if (selectedRole === 'Department Lead') {
                                  // If team is selected, use the specific team lead role
                                  if (volunteer.secondaryArea && volunteer.businessUnit) {
                                    const normalizedDept = normalizeDepartment(volunteer.businessUnit);
                                    const teamLeadRole = getManagerRoleForTeam(normalizedDept, volunteer.secondaryArea);
                                    actualRole = teamLeadRole || 'Department Lead';
                                  } else {
                                    actualRole = 'Department Lead';
                                  }
                                } else if (selectedRole === 'Director') {
                                  actualRole = 'Department Director';
                                }
                                
                                await handleComplianceUpdate(volunteer.id, 'assignedRole', actualRole);
                              }}
                              style={{ width: '100%' }}
                            >
                              <option value="">Select Role</option>
                              {['Volunteer', 'Department Lead', 'Director', 'Board Member'].map(role => (
                                <option key={role} value={role}>{role}</option>
                              ))}
                            </Select>
                          </div>
              </div>
              
                        {/* Areas of Interest */}
                        <div>
                          <div style={{ fontSize: '12px', color: tokens.colorNeutralForeground3, marginBottom: '4px' }}>
                            Areas of Interest
                    </div>
                          {volunteer.volunteerRoleInterest ? (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
                              {Array.isArray(volunteer.volunteerRoleInterest) 
                                ? (volunteer.volunteerRoleInterest as string[])?.map((interest: string, index: number) => (
                                    <div 
                                      key={index}
                                      className={styles.roleTag}
                                      style={{ 
                                        backgroundColor: tokens.colorPaletteTealBackground2,
                                        minWidth: '80px',
                                        height: '24px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        textAlign: 'center',
                                        fontSize: '11px',
                                        padding: '4px 8px'
                                      }}
                                    >
                                      {interest.trim()}
                    </div>
                                  ))
                                : (volunteer.volunteerRoleInterest as string)?.split(',').map((interest: string, index: number) => (
                                    <div 
                                      key={index}
                                      className={styles.roleTag}
                                      style={{ 
                                        backgroundColor: tokens.colorPaletteTealBackground2,
                                        minWidth: '80px',
                                        height: '24px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        textAlign: 'center',
                                        fontSize: '11px',
                                        padding: '4px 8px'
                                      }}
                                    >
                                      {interest.trim()}
                                    </div>
                                  ))
                            }
                            </div>
                          ) : (
                            <div style={{ color: tokens.colorNeutralForeground3, fontStyle: 'italic', fontSize: '12px' }}>
                              No areas of interest specified
                      </div>
                    )}
                    </div>
                      </div>
                    </div>
                  
                  {/* Additional Details Section (moved inside contactInfo wrapper so its bottom border extends past this) */}
                  <div style={{ marginTop: '20px', paddingTop: '20px' }}>
                    <div style={{ fontWeight: tokens.fontWeightSemibold, marginBottom: '12px' }}>
                      Additional Information
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '12px' }}>
                      <div style={{ padding: '8px', backgroundColor: tokens.colorNeutralBackground2, borderRadius: '4px' }}>
                        <div style={{ fontSize: '12px', color: tokens.colorNeutralForeground3, marginBottom: '2px' }}>
                          Campus
                        </div>
                        <div style={{ fontSize: '14px', color: tokens.colorNeutralForeground1 }}>
                          {volunteer.campus || 'Not specified'}
                        </div>
                      </div>
                      <div style={{ padding: '8px', backgroundColor: tokens.colorNeutralBackground2, borderRadius: '4px' }}>
                        <div style={{ fontSize: '12px', color: tokens.colorNeutralForeground3, marginBottom: '2px' }}>
                          Onboarding Group
                        </div>
                        <Select
                          value={volunteer.assignedManager || ''}
                          onChange={(e, data) => handleComplianceUpdate(volunteer.id, 'assignedManager', data.value || '')}
                          style={{ width: '100%' }}
                        >
                          <option value="">Select Onboarding Group</option>
                          <option value="Verification">Verification</option>
                          <option value="Compliance">Compliance</option>
                          <option value="Scheduling">Scheduling</option>
                        </Select>
                      </div>
                      <div style={{ padding: '8px', backgroundColor: tokens.colorNeutralBackground2, borderRadius: '4px' }}>
                        <div style={{ fontSize: '12px', color: tokens.colorNeutralForeground3, marginBottom: '2px' }}>
                          T-Shirt Size
                    </div>
                        <div style={{ fontSize: '14px', color: tokens.colorNeutralForeground1 }}>
                          {volunteer.volunteerTShirtSize || 'Not specified'}
                    </div>
                    </div>
                      <div style={{ padding: '8px', backgroundColor: tokens.colorNeutralBackground2, borderRadius: '4px' }}>
                        <div style={{ fontSize: '12px', color: tokens.colorNeutralForeground3, marginBottom: '2px' }}>
                          Birthdate
                    </div>
                        <div style={{ fontSize: '14px', color: tokens.colorNeutralForeground1 }}>
                          {volunteer.volunteerBirthdate || 'Not specified'}
                    </div>
                    </div>
                      <div style={{ padding: '8px', backgroundColor: tokens.colorNeutralBackground2, borderRadius: '4px' }}>
                        <div style={{ fontSize: '12px', color: tokens.colorNeutralForeground3, marginBottom: '2px' }}>
                          Preferred Contact Method
                        </div>
                        <div style={{ fontSize: '14px', color: tokens.colorNeutralForeground1 }}>
                          {volunteer.preferredContact || 'Not specified'}
                        </div>
                      </div>
                      <div style={{ padding: '8px', backgroundColor: tokens.colorNeutralBackground2, borderRadius: '4px' }}>
                        <div style={{ fontSize: '12px', color: tokens.colorNeutralForeground3, marginBottom: '2px' }}>
                          Best Time to Contact
                        </div>
                        <div style={{ fontSize: '14px', color: tokens.colorNeutralForeground1 }}>
                          {volunteer.bestTimeToContact || 'Not specified'}
                        </div>
                      </div>
                      <div style={{ padding: '8px', backgroundColor: tokens.colorNeutralBackground2, borderRadius: '4px' }}>
                        <div style={{ fontSize: '12px', color: tokens.colorNeutralForeground3, marginBottom: '2px' }}>
                          Contact Status
                        </div>
                        <Select
                          value={volunteer.volunteerStatus || ''}
                          onChange={(e, data) => handleComplianceUpdate(volunteer.id, 'volunteerStatus', data.value || '')}
                          style={{ width: '100%', fontSize: '14px' }}
                        >
                          <option value={VolunteerStatus.NEW_NEEDS_CONTACT}>New - Needs Contact</option>
                          <option value={VolunteerStatus.CONTACT_COMPLETE}>Active</option>
                          <option value={VolunteerStatus.CONTACTED_NO_RESPONSE}>Contacted - No Response</option>
                          <option value={VolunteerStatus.INACTIVE}>Inactive</option>
                        </Select>
                      </div>
                    </div>
                  </div>
                  {volunteer.mailingAddress && hasRole(['admin', 'executive']) && (
                    <div style={{ marginTop: '12px', padding: '8px', backgroundColor: tokens.colorNeutralBackground2, borderRadius: '4px' }}>
                      <div style={{ fontSize: '12px', color: tokens.colorNeutralForeground3, marginBottom: '2px' }}>
                        Mailing Address
                      </div>
                      <div style={{ fontSize: '14px', color: tokens.colorNeutralForeground1, whiteSpace: 'pre-line' }}>
                        {volunteer.mailingAddress}
                      </div>
                    </div>
                  )}
                  {volunteer.additionalInformation && (
                    <div style={{ marginTop: '12px', padding: '8px', backgroundColor: tokens.colorNeutralBackground2, borderRadius: '4px' }}>
                      <div style={{ fontSize: '12px', color: tokens.colorNeutralForeground3, marginBottom: '2px' }}>
                        Additional Information
                      </div>
                      <div style={{ fontSize: '14px', color: tokens.colorNeutralForeground1 }}>
                        {volunteer.additionalInformation}
                      </div>
                    </div>
                  )}
                  </div>
                    
                    {/* Compliance Status Section */}
                  <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: `1px solid ${tokens.colorNeutralStroke2}` }}>
                    <div style={{ fontWeight: tokens.fontWeightSemibold, marginBottom: '16px' }}>
                      Compliance Status
                    </div>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '12px' }}>
                      {/* 1. Orientation Completed */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input type="checkbox" checked={Boolean(volunteer.orientationCompleted)} onChange={(e) => handleComplianceUpdate(volunteer.id, 'orientationCompleted', e.target.checked)} style={{ width: '16px', height: '16px', cursor: 'pointer' }} />
                        <span style={{ fontSize: '14px', cursor: 'pointer' }} onClick={() => handleComplianceUpdate(volunteer.id, 'orientationCompleted', !Boolean(volunteer.orientationCompleted))}>Orientation Completed</span>
                      </div>
                      {/* 2. Background Cleared */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input type="checkbox" checked={Boolean(volunteer.backgroundCleared)} onChange={(e) => handleComplianceUpdate(volunteer.id, 'backgroundCleared', e.target.checked)} style={{ width: '16px', height: '16px', cursor: 'pointer' }} />
                        <span style={{ fontSize: '14px', cursor: 'pointer' }} onClick={() => handleComplianceUpdate(volunteer.id, 'backgroundCleared', !Boolean(volunteer.backgroundCleared))}>Background Cleared</span>
                      </div>
                      {/* 3. Training Complete (maps to both HIPAA flags) */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input type="checkbox" checked={Boolean(volunteer.hipaaPrivacyTrainingCompleted) || Boolean(volunteer.hipaaSecurityTrainingCompleted)} onChange={(e) => { const on = e.target.checked; handleComplianceUpdate(volunteer.id, 'hipaaPrivacyTrainingCompleted', on ? 'Yes' : ''); handleComplianceUpdate(volunteer.id, 'hipaaSecurityTrainingCompleted', on ? 'Yes' : ''); }} style={{ width: '16px', height: '16px', cursor: 'pointer' }} />
                        <span style={{ fontSize: '14px', cursor: 'pointer' }} onClick={() => { const on = !(Boolean(volunteer.hipaaPrivacyTrainingCompleted) || Boolean(volunteer.hipaaSecurityTrainingCompleted)); handleComplianceUpdate(volunteer.id, 'hipaaPrivacyTrainingCompleted', on ? 'Yes' : ''); handleComplianceUpdate(volunteer.id, 'hipaaSecurityTrainingCompleted', on ? 'Yes' : ''); }}>Training Complete</span>
                      </div>
                      {/* 4. In WhatsApp */}
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <input type="checkbox" checked={Boolean(volunteer.addedToWhatsapp)} onChange={(e) => handleComplianceUpdate(volunteer.id, 'addedToWhatsapp', e.target.checked)} style={{ width: '16px', height: '16px', cursor: 'pointer' }} />
                        <span style={{ fontSize: '14px', cursor: 'pointer' }} onClick={() => handleComplianceUpdate(volunteer.id, 'addedToWhatsapp', !Boolean(volunteer.addedToWhatsapp))}>In WhatsApp</span>
                      </div>
                    </div>
                  </div>
                  {/* Comments Section */}
                  <div style={{ marginTop: '24px', paddingTop: '24px', borderTop: `1px solid ${tokens.colorNeutralStroke2}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
                      <div style={{ fontWeight: tokens.fontWeightSemibold }}>
                        Team Comments ({comments.length})
                      </div>
                      <Button
                        appearance="subtle"
                        icon={commentsExpanded ? <ChevronUpRegular /> : <ChevronDownRegular />}
                        onClick={() => setCommentsExpanded(!commentsExpanded)}
                        size="small"
                      >
                        {commentsExpanded ? 'Collapse' : 'Expand'}
                      </Button>
                    </div>
                    {commentsExpanded && (
                      <>
                        {loadingComments ? (
                          <div style={{ textAlign: 'center', padding: '20px' }}>
                            <Spinner size="small" label="Loading comments..." />
                          </div>
                        ) : comments.length > 0 ? (
                          <div style={{ marginBottom: '16px' }}>
                            {comments.map((comment) => (
                              <div key={comment.id} style={{ 
                                padding: '12px', 
                                border: `1px solid ${tokens.colorNeutralStroke1}`, 
                                borderRadius: '4px', 
                                marginBottom: '8px',
                                backgroundColor: tokens.colorNeutralBackground1
                              }}>
                                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                                  <span style={{ fontWeight: tokens.fontWeightSemibold, fontSize: '12px' }}>
                                    {comment.author}
                                  </span>
                                  <span style={{ fontSize: '11px', color: tokens.colorNeutralForeground3 }}>
                                    {new Date(comment.timestamp).toLocaleDateString()} at {new Date(comment.timestamp).toLocaleTimeString()}
                                  </span>
                                </div>
                                <div style={{ fontSize: '14px', color: tokens.colorNeutralForeground1 }}>
                                  {comment.content}
                                </div>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <div style={{ 
                            textAlign: 'center', 
                            padding: '20px', 
                            color: tokens.colorNeutralForeground3, 
                            fontStyle: 'italic' 
                          }}>
                            No comments yet. Add the first comment below.
                          </div>
                        )}
                        
                        {/* Add Comment Section */}
                        <div>
                          <Textarea
                            value={newComment}
                            onChange={(e) => setNewComment(e.target.value)}
                            placeholder="Add a comment about this volunteer..."
                            rows={4}
                            style={{ 
                              width: '100%', 
                              marginBottom: '12px',
                              padding: '8px',
                              borderRadius: '4px',
                              border: `1px solid ${tokens.colorNeutralStroke1}`,
                              fontSize: '14px',
                              fontFamily: 'inherit',
                              resize: 'vertical'
                            }}
                            disabled={submittingComment}
                          />
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <Button
                              appearance="primary"
                              size="small"
                              onClick={handleAddComment}
                              disabled={!newComment.trim() || submittingComment}
                            >
                              {submittingComment ? (
                                <>
                                  <Spinner size="tiny" />
                                  Adding...
                                </>
                              ) : (
                                'Add Comment'
                              )}
                            </Button>
                            <Button
                              appearance="subtle"
                              size="small"
                              onClick={() => setNewComment('')}
                              disabled={submittingComment}
                            >
                              Clear
                            </Button>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                  </div>
                  </DialogContent>
                </DialogBody>
              </DialogSurface>
            </Dialog>
          )}
          
          
      
      {/* Avatar Upload Modal */}
      {showAvatarModal && (
        <div className={styles.fullScreenModal}>
          <div className={styles.modalOverlay} onClick={() => setShowAvatarModal(false)} />
          <div className={styles.avatarModalContent}>
            <div className={styles.modalHeader}>
              <h2>Update {volunteer.name}'s Photo</h2>
              <Button
                appearance="subtle"
                icon={<DismissRegular />}
                onClick={() => setShowAvatarModal(false)}
                size="small"
              >
                Close
              </Button>
            </div>
            
            <div className={styles.modalBody}>
              <div className={styles.avatarUploadSection}>
                <div className={styles.currentAvatar}>
                  <h3>Current Photo</h3>
                  <div className={styles.avatarPreview}>
                    {volunteer.avatarUrl ? (
                      <img 
                        src={volunteer.avatarUrl} 
                        alt={volunteer.name}
                        className={styles.avatarPreviewImage}
                      />
                    ) : (
                      <div className={styles.avatarPreviewPlaceholder}>
                        {volunteer.name?.charAt(0)?.toUpperCase() || 'V'}
                      </div>
                    )}
                  </div>
                </div>
                
                <div className={styles.uploadSection}>
                  <h3>Upload New Photo</h3>
                <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setAvatarFile(e.target.files?.[0] || null)}
                    className={styles.fileInput}
                    id="avatar-upload"
                  />
                  <label htmlFor="avatar-upload" className={styles.fileInputLabel}>
                    Choose Photo
                  </label>
                  
                  {avatarFile && (
                    <div className={styles.filePreview}>
                      <img 
                        src={URL.createObjectURL(avatarFile)} 
                        alt="Preview"
                        className={styles.filePreviewImage}
                      />
                      <div className={styles.fileInfo}>
                        <span>{avatarFile.name}</span>
                        <span>{(avatarFile.size / 1024 / 1024).toFixed(2)} MB</span>
              </div>
                    </div>
                  )}
                  
                  <div className={styles.uploadActions}>
                    <Button
                      appearance="primary"
                      onClick={handleAvatarUpload}
                      disabled={!avatarFile || uploadingAvatar}
                    >
                      {uploadingAvatar ? (
                        <>
                          <Spinner size="tiny" />
                          Uploading...
                        </>
                      ) : (
                        'Upload Photo'
                      )}
                    </Button>
                    <Button
                      appearance="subtle"
                      onClick={() => setAvatarFile(null)}
                      disabled={uploadingAvatar}
                    >
                      Clear
                    </Button>
              </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </Card>
  );
};

const VolunteerRoster: React.FC = () => {
  const styles = useStyles();
  const { user } = useAuth();
  const { getDirectorsForDepartment } = useDepartmentDirectors();

  const [volunteers, setVolunteers] = useState<Volunteer[]>([]);
  const [filteredVolunteers, setFilteredVolunteers] = useState<Volunteer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [lastRefresh, setLastRefresh] = useState(new Date());
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all'); // Default to all, will be set to Active in useEffect
  const [onboardingStatusFilter, setOnboardingStatusFilter] = useState('all');
  const [businessUnitFilter, setBusinessUnitFilter] = useState('all');
  const [buManagerFilter, setBuManagerFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  const [adminViewMode, setAdminViewMode] = useState<'manager' | 'onboarding'>('manager');
  const location = useLocation();

  const loadVolunteers = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const data = await getSharePointService().getVolunteers();
      setVolunteers(data);
      setLastRefresh(new Date());
    } catch (err) {
      console.error('Error loading volunteers:', err);
      setError('Failed to load volunteers. Please try again.');
    } finally {
      setLoading(false);
    }
  }, []);

  const updateVolunteer = useCallback(async (id: string, updates: Partial<Volunteer>) => {
    try {
      // Update local state immediately for instant UI feedback
      setVolunteers(prevVolunteers => 
        prevVolunteers.map(volunteer => 
          volunteer.id === id 
            ? { ...volunteer, ...updates }
            : volunteer
        )
      );
      
      // Update filtered volunteers as well
      setFilteredVolunteers(prevFiltered => 
        prevFiltered.map(volunteer => 
          volunteer.id === id 
            ? { ...volunteer, ...updates }
            : volunteer
        )
      );
      
      // Make the backend call
      await getSharePointService().updateVolunteer(id, updates);
      console.log('✅ Backend update completed successfully');
    } catch (error) {
      console.error('❌ Error updating volunteer:', error);
      // Revert the local state if the backend update failed
      await loadVolunteers(); // Refresh from backend to revert changes
      throw error;
    }
  }, [loadVolunteers]);

  const filterVolunteers = useCallback(() => {
    let filtered = [...volunteers];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(volunteer =>
        volunteer.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        volunteer.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        volunteer.phone?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Role-based filtering
    const isAdmin = user?.role === 'admin';
    const effectiveRole = isAdmin && adminViewMode === 'manager' ? 'manager' : user?.role;

    if (effectiveRole === 'manager' || effectiveRole === 'executive') {
      // For managers/executives, use business unit filters instead of status/onboarding filters
      if (businessUnitFilter !== 'all') {
        filtered = filtered.filter(volunteer => volunteer.businessUnit === businessUnitFilter);
      }

      if (buManagerFilter !== 'all') {
        filtered = filtered.filter(volunteer => {
          // Use department directors from SharePoint (matching OrgChart approach)
          const normalizedDept = normalizeDepartment(volunteer.businessUnit);
          if (!normalizedDept) return false;
          const directors = getDirectorsForDepartment(normalizedDept);
          // Check if any director matches the filter
          return directors.some(dir => dir === buManagerFilter);
        });
      }

      if (roleFilter !== 'all') {
        filtered = filtered.filter(volunteer => volunteer.assignedRole === roleFilter);
      }

      // Add status and onboarding filters for manager view as well
      if (statusFilter !== 'all') {
        filtered = filtered.filter(volunteer => volunteer.volunteerStatus === statusFilter);
      }

      if (onboardingStatusFilter !== 'all') {
        filtered = filtered.filter(volunteer => volunteer.assignedManager === onboardingStatusFilter);
      }
    } else {
      // For other roles, use status and onboarding filters
    if (statusFilter !== 'all') {
      filtered = filtered.filter(volunteer => volunteer.volunteerStatus === statusFilter);
    }

    if (onboardingStatusFilter !== 'all') {
      filtered = filtered.filter(volunteer => volunteer.assignedManager === onboardingStatusFilter);
      }
    }

    // Sort volunteers alphabetically by name
    filtered.sort((a, b) => {
      const nameA = (a.name || '').toLowerCase();
      const nameB = (b.name || '').toLowerCase();
      return nameA.localeCompare(nameB);
    });

    setFilteredVolunteers(filtered);
  }, [volunteers, searchTerm, statusFilter, onboardingStatusFilter, businessUnitFilter, buManagerFilter, roleFilter, user?.role, adminViewMode, getDirectorsForDepartment]);

  useEffect(() => {
    filterVolunteers();
  }, [filterVolunteers]);

  // Apply updates locally without full reload; server data stays in sync via the writer
  useEffect(() => {
    const handler = (e: Event) => {
      const ce = e as CustomEvent<{ volunteerId: string; updates: Partial<Volunteer> }>;
      const { volunteerId, updates } = ce.detail || ({} as any);
      if (!volunteerId) return;
      setVolunteers(prev => prev.map(v => v.id === volunteerId ? { ...v, ...updates } : v));
      setFilteredVolunteers(prev => prev.map(v => v.id === volunteerId ? { ...v, ...updates } : v));
    };
    window.addEventListener('volunteer-updated', handler as EventListener);
    return () => window.removeEventListener('volunteer-updated', handler as EventListener);
  }, []);

  // Pre-filter from query params: department and role
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const dept = params.get('department');
    const role = params.get('role');
    if (dept) setBusinessUnitFilter(dept);
    if (role) setRoleFilter(role);
  }, [location.search]);

  useEffect(() => {
    loadVolunteers();
  }, [loadVolunteers]);

  // Set default filter to Active after component mounts
  useEffect(() => {
    setStatusFilter(VolunteerStatus.CONTACT_COMPLETE);
  }, []);

  const getVolunteerStats = () => {
    const total = volunteers.length;
    const active = volunteers.filter(v => v.volunteerStatus === VolunteerStatus.CONTACT_COMPLETE).length;
    const inactive = volunteers.filter(v => v.volunteerStatus === VolunteerStatus.INACTIVE).length;
    const pending = volunteers.filter(v => v.volunteerStatus === VolunteerStatus.NEW_NEEDS_CONTACT).length;

    return { total, active, inactive, pending };
  };



  if (loading) {
    return (
      <div className={styles.container}>
        <Title2>Loading volunteers...</Title2>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.headerTop}>
          <div className={styles.titleContainer}>
        <Title2 className={styles.title}>Volunteer Roster</Title2>
          <Body1 className={styles.subtitle}>
              Manage and communicate with clinic volunteers
            </Body1>
          </div>
          <div className={styles.headerActions}>
            <div className={styles.lastUpdated}>
              <ClockRegular style={{ fontSize: '14px', color: tokens.colorNeutralForeground3 }} />
              <Body1 className={styles.lastUpdatedText}>
            Last updated: {lastRefresh.toLocaleTimeString()}
          </Body1>
        </div>
            <div className={styles.actionButtons}>
              {user?.role === 'admin' && (
                <div className={styles.adminToggle}>
                  <span style={{ fontSize: '14px', marginRight: '8px', color: tokens.colorNeutralForeground2 }}>
                    View Mode:
                  </span>
                  <button 
                    className={`${styles.actionButton} ${adminViewMode === 'manager' ? styles.activeToggle : ''}`}
                    onClick={() => setAdminViewMode('manager')}
                    title="Manager View"
                  >
                    Manager
                  </button>
                  <button 
                    className={`${styles.actionButton} ${adminViewMode === 'onboarding' ? styles.activeToggle : ''}`}
                    onClick={() => setAdminViewMode('onboarding')}
                    title="Onboarding View"
                  >
                    Onboarding
                  </button>
                </div>
              )}
              <button 
                className={styles.actionButton}
            onClick={loadVolunteers}
                title="Refresh Data"
          >
                <ArrowClockwiseRegular style={{ fontSize: '14px' }} />
            Refresh
              </button>
              <button 
                className={styles.actionButton}
                onClick={() => console.log('Export to Excel')}
                title="Export to Excel"
              >
                <ArrowDownloadRegular style={{ fontSize: '14px' }} />
                Export
              </button>
            </div>
          </div>
        </div>
      </div>
        
      {error && (
        <div style={{ 
          backgroundColor: tokens.colorPaletteRedBackground2, 
          color: tokens.colorPaletteRedForeground2,
          padding: '12px',
          borderRadius: '4px',
          marginBottom: '16px'
        }}>
          {error}
        </div>
      )}

      {/* Volunteer Statistics */}
      <div className={styles.stats}>
        {(() => {
          const stats = getVolunteerStats();
          return (
            <>
              <div className={styles.statCard}>
                <div className={styles.statIcon} style={{ backgroundColor: tokens.colorPaletteBlueBackground2 }}>
                  <PeopleRegular />
                </div>
                <div className={styles.statContent}>
                <div className={styles.statValue}>{stats.total}</div>
                <div className={styles.statLabel}>Total Volunteers</div>
                </div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.statIcon} style={{ backgroundColor: tokens.colorPaletteGreenBackground2 }}>
                  <CheckmarkRegular />
                </div>
                <div className={styles.statContent}>
                <div className={styles.statValue}>{stats.active}</div>
                <div className={styles.statLabel}>Active</div>
                </div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.statIcon} style={{ backgroundColor: tokens.colorPaletteRedBackground2 }}>
                  <DismissCircleRegular />
                </div>
                <div className={styles.statContent}>
                <div className={styles.statValue}>{stats.inactive}</div>
                <div className={styles.statLabel}>Inactive</div>
                </div>
              </div>
              <div className={styles.statCard}>
                <div className={styles.statIcon} style={{ backgroundColor: tokens.colorPaletteYellowBackground2 }}>
                  <DataTrendingRegular />
                </div>
                <div className={styles.statContent}>
                <div className={styles.statValue}>{stats.pending}</div>
                <div className={styles.statLabel}>Pending Contact</div>
                </div>
              </div>
            </>
          );
        })()}
      </div>

      {/* Filters */}
      <div className={styles.filters}>
        <Input
          placeholder="Search volunteers..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={styles.searchInput}
          contentBefore={<SearchRegular />}
        />
        
        {/* Role-based filters */}
        {(user?.role === 'manager' || user?.role === 'executive' || (user?.role === 'admin' && adminViewMode === 'manager')) ? (
          // Manager/Executive filters - Primary row
          <>
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
              <Select
                value={businessUnitFilter}
                onChange={(e: any) => setBusinessUnitFilter(e.target.value)}
                className={styles.filterSelect}
              >
                <option value="all">All Departments</option>
                <option value="Marketing and Communications">Marketing and Communications</option>
                <option value="Clinical Operations">Clinical Operations</option>
                <option value="Finance & Development">Finance & Development</option>
                <option value="LEHR">Legal & HR (LEHR)</option>
                <option value="Support Services">Support Services</option>
                <option value="Technology">Technology</option>
              </Select>
              <Select
                value={roleFilter}
                onChange={(e: any) => setRoleFilter(e.target.value)}
                className={styles.filterSelect}
              >
                <option value="all">All Roles</option>
                {(businessUnitFilter === 'all' 
                  ? ['Volunteer', 'Communications Manager', 'Community/Event Manager', 'Medical Director (Providers)', 'Patient Advocate Manager', 'Medical Services Manager', 'Administration Manager', 'Engagement Manager', 'Operations Manager', 'Language Services Manager', 'Finance Manager', 'Donor Development Manager', 'Procurement Manager', 'Tax/Audit Manager', 'Compliance/Risk Manager', 'Evaluation Manager', 'HR Manager', 'Spiritual Care Manager', 'Volunteer Care Manager', 'Systems Management Manager', 'Technical Support Manager', 'EHR Manager']
                  : getAvailableRoles(businessUnitFilter)
                ).map(role => (
                  <option key={role} value={role}>{role}</option>
                ))}
              </Select>
            </div>
            
            {/* Secondary row */}
            <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
              <Select
                value={buManagerFilter}
                onChange={(e: any) => setBuManagerFilter(e.target.value)}
                className={styles.filterSelect}
              >
                <option value="all">All Managers</option>
                {(() => {
                  // Get unique department directors from all volunteers (matching OrgChart approach)
                  const directorSet = new Set<string>();
                  volunteers
                    .filter(v => (businessUnitFilter === 'all' || v.businessUnit === businessUnitFilter))
                    .forEach(v => {
                      const normalizedDept = normalizeDepartment(v.businessUnit);
                      if (normalizedDept) {
                        const directors = getDirectorsForDepartment(normalizedDept);
                        directors.forEach(dir => {
                          if (dir && dir !== 'Not assigned') {
                            directorSet.add(dir);
                          }
                        });
                      }
                    });
                  return Array.from(directorSet).sort().map(director => (
                    <option key={director} value={director}>{director}</option>
                  ));
                })()}
              </Select>
              <Select
                value={statusFilter}
                onChange={(e: any) => setStatusFilter(e.target.value)}
                className={styles.filterSelect}
              >
                <option value="all">All Contact Status</option>
                <option value={VolunteerStatus.CONTACT_COMPLETE}>Active</option>
                <option value={VolunteerStatus.NEW_NEEDS_CONTACT}>New Needs Contact</option>
                <option value={VolunteerStatus.CONTACTED_NO_RESPONSE}>Contacted, No Response</option>
                <option value={VolunteerStatus.INACTIVE}>Inactive</option>
              </Select>
              <Select
                value={onboardingStatusFilter}
                onChange={(e: any) => setOnboardingStatusFilter(e.target.value)}
                className={styles.filterSelect}
              >
                <option value="all">All Onboarding Group</option>
                <option value="Compliance">Compliance</option>
                <option value="Scheduling">Scheduling</option>
                <option value="Verification">Verification</option>
              </Select>
            </div>
          </>
        ) : (
          // Onboarding/Compliance filters
          <>
            <Select
              value={statusFilter}
              onChange={(e: any) => setStatusFilter(e.target.value)}
              className={styles.filterSelect}
            >
              <option value="all">All Contact Status</option>
              <option value={VolunteerStatus.CONTACT_COMPLETE}>Active</option>
              <option value={VolunteerStatus.NEW_NEEDS_CONTACT}>New Needs Contact</option>
              <option value={VolunteerStatus.CONTACTED_NO_RESPONSE}>Contacted, No Response</option>
              <option value={VolunteerStatus.INACTIVE}>Inactive</option>
            </Select>
            <Select
              value={onboardingStatusFilter}
              onChange={(e: any) => setOnboardingStatusFilter(e.target.value)}
              className={styles.filterSelect}
            >
              <option value="all">All Onboarding Group</option>
              <option value="Compliance">Compliance</option>
              <option value="Scheduling">Scheduling</option>
              <option value="Verification">Verification</option>
            </Select>
          </>
        )}
      </div>

      {/* Volunteer Grid */}
      <div className={styles.volunteerGrid}>
        {filteredVolunteers.map(volunteer => (
          <RoleSpecificVolunteerCard
            key={volunteer.id}
            volunteer={volunteer}
            role={user?.role || 'volunteer'}
            onUpdate={updateVolunteer}
            adminViewMode={user?.role === 'admin' ? adminViewMode : undefined}
          />
        ))}
      </div>
    </div>
  );
};

export default VolunteerRoster; 